/**
 * Readable engine choices for the vehicle picker (Aperio preset).
 *
 * The catalogue names engines like "1.5L 4-CYL ENGINE CODE L15B7 9 TURBO":
 * a 2019 Civic offered several of those, and a customer cannot tell them
 * apart. Here each reads "1.5L 4-cyl turbo"; only when two choices would
 * read the same is the engine code added ("1.5L 4-cyl turbo · L15B7"), since
 * those rows can take a different amount of oil. A name that does not follow
 * the catalogue pattern is shown as it is.
 */

// eslint-disable-next-line max-len
const PATTERN = /^\s*(\d+(?:\.\d+)?)\s*L\s+(\d+)\s*-?\s*cyl\b\s*(?:engine\s+code\b)?\s*(.*)$/i;

/** Trailing words that describe the engine, in display form. */
const DESCRIPTORS: Record<string, string> = {
  turbo: 'turbo',
  supercharged: 'supercharged',
  diesel: 'diesel',
  flex: 'flex-fuel',
  hybrid: 'hybrid',
  electric: 'electric',
  cng: 'CNG',
};

interface ParsedEngine {
  base: string;
  code: string;
}

function parse(engine: string): ParsedEngine | null {
  const match = PATTERN.exec(engine || '');
  if (!match) return null;
  const [, litres, cylinders, rest] = match;
  const words: string[] = [];
  const codeParts: string[] = [];
  rest.split(/\s+/).filter(Boolean).forEach((token) => {
    const descriptor = DESCRIPTORS[token.toLowerCase()];
    if (descriptor) {
      if (!words.includes(descriptor)) words.push(descriptor);
      return;
    }
    // "[8]"-style markers and the one-character VIN engine digit are noise.
    if (/^\[.*\]$/.test(token) || token.length < 2) return;
    // Cam layout and market words are not an engine code.
    if (/^(sohc|dohc|canada|usa)$/i.test(token)) return;
    codeParts.push(token.toUpperCase());
  });
  return {
    base: [`${litres}L ${cylinders}-cyl`, ...words].join(' '),
    code: codeParts[0] || '',
  };
}

/** One label per engine, in the same order; equal names get their code. */
export function engineLabels(engines: string[]): string[] {
  const parsed = engines.map(parse);
  const baseCount = new Map<string, number>();
  parsed.forEach((p) => {
    if (p) baseCount.set(p.base, (baseCount.get(p.base) || 0) + 1);
  });
  const labels = engines.map((engine, i) => {
    const p = parsed[i];
    if (!p) return engine;
    if ((baseCount.get(p.base) || 0) < 2) return p.base;
    return p.code ? `${p.base} · ${p.code}` : engine;
  });
  // Still two the same (no usable code): those keep the catalogue's name.
  return labels.map((label, i) => (
    labels.indexOf(label) !== labels.lastIndexOf(label) ? engines[i] : label
  ));
}
